/**
 * Búsqueda difusa multi-palabra en cliente (JavaScript).
 *
 * Diseñada para reflejar el comportamiento del backend `search_utils.py`
 * de Beem: "cinta dos centimetros" debe encontrar "Cinta 2cm Blanco" incluso
 * cuando los tokens están en desorden o con acentos/sinónimos.
 *
 * Uso:
 *   fuzzyFilter(list, query, ['nombre', 'sku'])
 *   fuzzyMatch('Cinta 2cm Blanco', 'cinta blanco 2cm')
 */

const NUMBER_WORDS = {
  cero: "0",
  uno: "1", una: "1", un: "1",
  dos: "2",
  tres: "3",
  cuatro: "4",
  cinco: "5",
  seis: "6",
  siete: "7",
  ocho: "8",
  nueve: "9",
  diez: "10",
  once: "11",
  doce: "12",
  trece: "13",
  catorce: "14",
  quince: "15",
  dieciseis: "16",
  diecisiete: "17",
  dieciocho: "18",
  diecinueve: "19",
  veinte: "20",
  treinta: "30",
  cuarenta: "40",
  cincuenta: "50",
  sesenta: "60",
  setenta: "70",
  ochenta: "80",
  noventa: "90",
  cien: "100",
  ciento: "100",
};

const UNIT_SYNONYMS = {
  centimetros: "cm",
  centimetro: "cm",
  cms: "cm",
  milimetros: "mm",
  milimetro: "mm",
  metros: "mts",
  metro: "mts",
  mt: "mts",
  kilos: "kg",
  kilo: "kg",
  gramos: "g",
  gramo: "g",
  pulgadas: "in",
  pulgada: "in",
  yardas: "yd",
  yarda: "yd",
  unidades: "und",
  unidad: "und",
  paquete: "pqt",
  paquetes: "pqt",
  rollo: "roll",
  rollos: "roll",
  madejas: "madeja",
};

// Nov/2026: el punto/coma ENTRE dígitos es decimal ("1.2cm", "0,7mm") y se conserva como "1.2";
// antes se partía en "1 2" y cualquier "1" o "2" suelto hacía match (encajes 1.8cm salían por "cinta 1.2").
const DECIMAL_RE = /(\d)[.,](\d)/g;
const SEPARATORS_RE = /[-_/\\,·:;()[\]*+&%|<>="'`~^]+|\.(?!\d)|(?<!\d)\./g;
// Sinónimos de palabra completa (cómo lo dice el vendedor → cómo está en el catálogo)
const WORD_SYNONYMS = {
  cintas: "cinta", liston: "cinta", listones: "cinta", ribbon: "cinta",
  satin: "satinado", satinada: "satinado", satinadas: "satinado", satinados: "satinado",
  raso: "razo", papeles: "papel", encajes: "encaje", botones: "boton", cierres: "cierre",
  lanas: "lana", hilos: "hilo", agujas: "aguja", perlas: "perla", mostacillas: "mostacilla",
};
const NUMLETTER_RE = /(?<=\d)(?=[a-z])|(?<=[a-z])(?=\d)/g;
const MULTISPACE_RE = /\s+/g;

function stripAccents(s) {
  return String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function normalize(s) {
  if (!s) return "";
  let out = stripAccents(String(s)).toLowerCase();
  out = out.replace(DECIMAL_RE, "$1\u0000$2").replace(SEPARATORS_RE, " ").replace(/\u0000/g, ".").replace(NUMLETTER_RE, " ").replace(MULTISPACE_RE, " ").trim();
  if (!out) return "";
  const parts = [];
  for (const tok of out.split(" ")) {
    if (!tok) continue;
    if (Object.prototype.hasOwnProperty.call(NUMBER_WORDS, tok)) {
      parts.push(NUMBER_WORDS[tok]);
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(UNIT_SYNONYMS, tok)) {
      parts.push(UNIT_SYNONYMS[tok]);
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(WORD_SYNONYMS, tok)) {
      parts.push(WORD_SYNONYMS[tok]);
      continue;
    }
    parts.push(tok);
  }
  return parts.join(" ");
}

function compact(s) {
  return stripAccents(String(s || "")).toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function tokenize(q) {
  return normalize(q).split(" ").filter(Boolean);
}

function tokenMatches(padded, textNorm, t) {
  const hasDigit = /\d/.test(t);
  const estricto = t.length <= 2 || /^\d+$/.test(t) || (hasDigit && t.length <= 4);
  if (estricto) {
    return padded.indexOf(` ${t} `) !== -1;
  }
  if (textNorm.indexOf(t) !== -1) return true;
  // Jun/2026: singular/plural ("plastiflechas" encuentra "plastiflecha")
  const ts = stem(t);
  return ts !== t && textNorm.indexOf(ts) !== -1;
}

export function fuzzyMatch(text, query) {
  const toks = tokenize(query);
  if (toks.length === 0) return true;
  const textNorm = normalize(text);
  if (!textNorm) return false;
  const padded = ` ${textNorm} `;
  for (const t of toks) {
    if (!tokenMatches(padded, textNorm, t)) return false;
  }
  return true;
}

/**
 * Filtra una lista concatenando los campos indicados de cada objeto.
 * Devuelve la lista completa cuando la query está vacía.
 */
export function fuzzyFilter(list, query, fields) {
  const toks = tokenize(query);
  if (toks.length === 0) return list;
  const flds = fields && fields.length ? fields : ["nombre"];
  const out = [];
  for (const item of list) {
    const parts = [];
    for (const f of flds) {
      const v = item?.[f];
      if (v != null && v !== "") parts.push(String(v));
    }
    const textNorm = normalize(parts.join(" "));
    if (!textNorm) continue;
    const padded = ` ${textNorm} `;
    let ok = true;
    for (const t of toks) {
      if (!tokenMatches(padded, textNorm, t)) { ok = false; break; }
    }
    if (ok) out.push(item);
  }
  return out;
}

// -----------------------------------------------------------------------
// Oct/2026 — Búsqueda scored con fallback para typos y matches parciales.
// Flujo:
//   1) Prueba match estricto (todos los tokens matchean) → score alto.
//   2) Si no, score por tokens que SÍ matchean (parcial OK, >= 50% matchean).
//   3) Si no, score por similitud carácter a carácter (Levenshtein corto)
//      sobre nombre/sku/código → tolera typos "satianado" vs "satinado".
// Resultado ordenado por score desc, limitado a 50 por default.
// -----------------------------------------------------------------------

function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  if (Math.abs(a.length - b.length) > 3) return 999; // early exit — no vale la pena calcular typos lejanos
  const dp = Array(b.length + 1).fill(0).map((_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = dp[j];
      if (a[i - 1] === b[j - 1]) {
        dp[j] = prev;
      } else {
        dp[j] = Math.min(prev, dp[j], dp[j - 1]) + 1;
      }
      prev = tmp;
    }
  }
  return dp[b.length];
}

// Jun/2026: raíz singular/plural ("plastiflechas" ≈ "plastiflecha", "rollos" ≈ "rollo",
// "lentejuelas" ≈ "lentejuela"). Solo para palabras sin dígitos y >= 4 chars.
function stem(w) {
  if (!w || /\d/.test(w) || w.length < 4) return w;
  if (w.endsWith("ces")) return w.slice(0, -3) + "z"; // lapices → lapiz
  if (w.endsWith("es") && w.length >= 6 && !/[aeiou]es$/.test(w)) return w.slice(0, -2); // flores → flor
  if (w.endsWith("s")) return w.slice(0, -1);
  return w;
}

function tokenScore(padded, textNorm, t) {
  const hasDigit = /\d/.test(t);
  // Match exacto como palabra completa (espacios alrededor)
  if (padded.indexOf(` ${t} `) !== -1) return 100;
  // Nov/2026: equivalencia numérica — "91" encuentra "0091", "7" encuentra "07"
  if (/^\d+(\.\d+)?$/.test(t)) {
    const n = parseFloat(t);
    for (const w of textNorm.split(" ")) {
      if (/^\d+(\.\d+)?$/.test(w) && parseFloat(w) === n) return 95;
    }
  }
  // Nov/2026 — RAÍZ DEL RUIDO: los números solo valen EXACTOS. Antes "1" era prefijo de "12cm"/"18cm",
  // "2" de "20cm"/"25cm" y "4" de "40mm" → "cinta 4cm" traía cierres de 40mm y "1.2" traía encajes 1.8cm.
  if (hasDigit) return 0;
  // Mismo singular/plural como palabra completa
  const ts = stem(t);
  if (!hasDigit) {
    for (const w of textNorm.split(" ")) {
      if (w.length >= 4 && stem(w) === ts) return 96;
    }
  }
  // Prefijo de palabra ("rojo" encuentra "rojito")
  if (padded.indexOf(` ${t}`) !== -1) return 70;
  if (!hasDigit && ts !== t && padded.indexOf(` ${ts}`) !== -1) return 66;
  // Substring en cualquier parte
  if (!hasDigit && textNorm.indexOf(t) !== -1 && t.length >= 3) return 50;
  // Typo tolerado — solo para tokens >= 4 chars sin dígitos (evitamos falsos positivos en SKUs)
  if (!hasDigit && t.length >= 4) {
    const words = textNorm.split(" ");
    let best = 0;
    for (const w of words) {
      if (w.length < 3) continue;
      // Permitir máximo 1 typo por cada 4 chars
      const maxDist = t.length >= 7 ? 2 : 1;
      const d = levenshtein(t, w);
      if (d <= maxDist) {
        const sim = 1 - d / Math.max(t.length, w.length);
        best = Math.max(best, Math.round(sim * 40));
      }
    }
    if (best > 0) return best;
  }
  return 0;
}

/**
 * Búsqueda scored: tolera typos, matches parciales y campos múltiples.
 * Devuelve una lista ordenada por score desc. Si la query es vacía devuelve
 * la lista original sin cambios.
 *
 * Opciones:
 *   - limit: tope de resultados (default 50)
 *   - minScorePerToken: score mínimo por token para que el item sobreviva
 *     el filtrado soft (default 25 — permite typos y prefijos pero descarta ruido)
 *   - boostFields: campos cuyo match pesa más (ej. ['sku', 'codigo'])
 *   - strict: Feb/2026 — si true, EXIGE que todos los tokens matcheen.
 *     Es el modo correcto para búsqueda de producto por descripción, donde
 *     "cinta 4 cm verde" NO debe traer "cinta satin 3mm rosa".
 */
export function fuzzySearch(list, query, fields, opts = {}) {
  const toks = tokenize(query);
  if (toks.length === 0) return list;
  const flds = fields && fields.length ? fields : ["nombre"];
  const limit = opts.limit ?? 50;
  const minScorePerToken = opts.minScorePerToken ?? 25;
  const boostFields = new Set(opts.boostFields || ["sku", "codigo"]);
  const strict = opts.strict ?? false;

  // Nov/2026: modo CÓDIGO — "M02", "EF26-146", "PDE0227" se comparan compactos contra sku/código.
  // Si algún producto coincide por código, se devuelven SOLO esos (sin ruido de "m" → millare).
  const compactQ = compact(query);
  if (compactQ.length >= 2 && compactQ.length <= 14 && /^[a-z]+\d[a-z0-9]*$/.test(compactQ) && !/\s/.test(String(query).trim())) {
    const hits = [];
    for (const item of list) {
      let best = 0;
      for (const f of boostFields) {
        const c = compact(item?.[f]);
        if (!c) continue;
        if (c === compactQ) best = Math.max(best, 3);
        else if (c.startsWith(compactQ)) best = Math.max(best, 2);
        else if (compactQ.length >= 3 && c.endsWith(compactQ)) best = Math.max(best, 1);
      }
      if (best) hits.push({ item, score: best });
    }
    if (hits.length) {
      hits.sort((a, b) => b.score - a.score);
      return hits.slice(0, limit).map((h) => h.item);
    }
  }

  const results = [];
  // Jun/2026: peso por campo — el NOMBRE manda; descripción/color pesan menos para que
  // un producto que solo menciona la palabra en la descripción no le gane al que se llama así.
  const FIELD_WEIGHT = { nombre: 1.0, familia_key: 0.9, categoria_nombre: 0.85, categoria: 0.85, sku: 1.0, codigo: 1.0, color: 0.8, descripcion: 0.55 };
  // Nov/2026: "título" = nombre + familia + categoría. En prod las variantes se llaman solo por color
  // ("GRIS") y el "PAPEL COREANO" vive en la categoría/familia → debe pesar como el nombre.
  const TITULO_FIELDS = new Set(["nombre", "familia_key", "categoria_nombre", "categoria"]);
  for (const item of list) {
    const campos = [];
    let boostNorm = "";
    let nombreNorm = "";
    let tituloNorm = "";
    for (const f of flds) {
      const v = item?.[f];
      if (v == null || v === "") continue;
      const n = normalize(String(v));
      if (!n) continue;
      campos.push({ f, n, padded: ` ${n} `, w: FIELD_WEIGHT[f] ?? 0.7 });
      if (boostFields.has(f)) boostNorm += " " + n;
      if (f === "nombre") nombreNorm = n;
      if (TITULO_FIELDS.has(f)) tituloNorm += " " + n;
    }
    if (!campos.length) continue;
    boostNorm = boostNorm.trim();
    tituloNorm = tituloNorm.trim();
    const paddedBoost = ` ${boostNorm} `;
    const paddedNombre = ` ${tituloNorm} `;
    const nombreScoreNorm = tituloNorm;

    let total = 0;
    let tokensQueMachean = 0;
    let tokensEnNombre = 0;
    let tokensFuertes = 0; // Nov/2026: palabra completa / prefijo / plural (no solo typo o substring débil)
    let matchEnBoost = false;
    for (const t of toks) {
      let s = 0;
      for (const c of campos) {
        const cs = tokenScore(c.padded, c.n, t) * c.w;
        if (cs > s) s = cs;
      }
      if (s >= 60) tokensFuertes += 1;
      // Bonus si el match aparece en campos boost (SKU/código)
      if (s > 0 && boostNorm) {
        const boostHit = tokenScore(paddedBoost, boostNorm, t);
        if (boostHit > 0) { s += 50; matchEnBoost = true; }
      }
      // Bonus especial: SKU/código exacto completo
      if (boostNorm && boostNorm.split(" ").includes(t)) s += 150;
      if (tituloNorm && tokenScore(paddedNombre, nombreScoreNorm, t) >= 66) tokensEnNombre += 1;
      if (s >= minScorePerToken) tokensQueMachean += 1;
      total += s;
    }
    // Feb/2026: en modo strict TODOS los tokens tienen que matchear algo útil.
    // Sin strict, el fallback acepta >=50% para tolerar typos y queries largas.
    if (strict) {
      if (tokensQueMachean < toks.length) continue;
      // Nov/2026: al menos la mitad de las palabras deben coincidir "fuerte"; evita que
      // "papel coreano azul" traiga cosas que solo matchean por typos/substrings sueltos.
      if (toks.length >= 2 && tokensFuertes < Math.ceil(toks.length / 2)) continue;
      // …y al menos una palabra debe estar en el NOMBRE o código: lo que solo aparece en la
      // descripción no es lo que el vendedor está buscando.
      if (toks.length >= 2 && tokensEnNombre === 0 && !matchEnBoost) continue;
    } else {
      const umbralTokens = Math.ceil(toks.length / 2);
      if (tokensQueMachean < umbralTokens) continue;
    }
    if (tokensQueMachean === toks.length) total += 25;
    // Nov/2026: cada palabra que está en el NOMBRE pesa mucho más que una que solo está en la
    // descripción/familia → "cinta 4cm" trae primero lo que se llama "…4CM", no cierres que
    // mencionan "cinta" en su descripción.
    total += tokensEnNombre * 80;
    if (tokensEnNombre === toks.length) total += 40;
    if (nombreNorm) {
      const q = toks.join(" ");
      const qs = toks.map(stem).join(" ");
      if (nombreNorm.startsWith(q) || nombreNorm.split(" ").map(stem).join(" ").startsWith(qs)) total += 60;
    }
    results.push({ item, score: total });
  }
  results.sort((a, b) => b.score - a.score);
  return results.slice(0, limit).map((r) => r.item);
}
